import type { Workspace } from "@/types/api";

// Save-workspace helpers for the new studio: duplicate-name detection for
// the modal and the workspace payload the studio can fill today (the old
// /studio's Workspace shape — pipeline is its JSON string). This module
// stays provider-free, so its tests run without the React import graph.

/** Whether a workspace with this name already exists — trimmed and
 *  case-insensitive, so "orders" can't slip past "Orders ". */
export function workspaceNameTaken(
  name: string,
  existing: Array<{ name: string }>,
): boolean {
  const target = name.trim().toLowerCase();
  if (target === "") return false;
  return existing.some((w) => w.name.trim().toLowerCase() === target);
}

/** The name "Duplicate and continue" saves under, no prompt: the base
 *  itself when free (scratch work saving for the first time), else
 *  `-CLONE`, `-CLONE-2`, `-CLONE-3`, … — the first the given lists don't
 *  hold (same trimmed, case-insensitive comparison the name modal blocks
 *  on). Duplicating an open pipeline always suffixes: its own name holds
 *  the base. */
export function freeDuplicateName(
  base: string,
  taken: Array<{ name: string }>,
): string {
  if (!workspaceNameTaken(base, taken)) return base;
  for (let n = 1; ; n++) {
    const candidate = n === 1 ? `${base}-CLONE` : `${base}-CLONE-${n}`;
    if (!workspaceNameTaken(candidate, taken)) return candidate;
  }
}

/** What scratch work's save-and-continue is named after: the pipeline
 *  document's own name, or the template's default when the code doesn't
 *  carry a usable one. */
export function scratchBaseName(code: string): string {
  try {
    const name = (JSON.parse(code) as { name?: unknown })?.name;
    if (typeof name === "string" && name.trim() !== "") return name.trim();
  } catch {
    // Not JSON — the fallback below.
  }
  return "my-pipeline";
}

/** The stored fields carried over when renaming/duplicating a workspace —
 *  the server-side content, not the editor state. */
export function storedFields(ws: Workspace) {
  return {
    input: ws.input,
    input_origin: ws.input_origin,
    scripts: ws.scripts,
    schema: ws.schema,
    test_suite_id: ws.test_suite_id,
    pipeline: ws.pipeline,
  };
}

/** The create payload from what the new studio holds: the pipeline code
 *  (draft or generated) and the input document. */
export function buildWorkspacePayload(args: {
  name: string;
  inputDocument: string;
  pipelineCode: string;
}): Partial<Workspace> & { name: string } {
  return {
    name: args.name,
    input: args.inputDocument === "" ? null : args.inputDocument,
    // An empty Full view stores no pipeline (reopening lands on the ghost
    // template again), the same way an empty input stores null.
    pipeline: args.pipelineCode === "" ? null : args.pipelineCode,
  };
}

// ── LEGACY workspace test snapshots ───────────────────────
//
// Workspaces no longer write a private snapshot suite: test_suite_id is
// now a REFERENCE to a shared catalog suite (the one the Tests panel
// stages). The two helpers below survive only to keep the snapshot suites
// written by earlier builds OUT of the Explorer tree — nothing creates
// suites carrying this description any more.

/** The description marking a suite as a LEGACY workspace test snapshot. */
export const WORKSPACE_TESTS_DESCRIPTION = "workspace-tests";

/** Whether this is a LEGACY snapshot suite — the tree hides those. */
export function isWorkspaceTestsSuite(suite: {
  description?: string | null;
}): boolean {
  return suite.description === WORKSPACE_TESTS_DESCRIPTION;
}
